<template>
    <div>
        <div v-if="!isLoading">
            <div v-if="isBackgroundImageEditor" class="text-right mb-3">
                <UploadImageCommon
                    :classLabel="`btn-secondary`"
                    @dataFile="handleFileBackgroundEditor"
                    :title="`${$t('btn.addbackgroundEditor')}`"
                    :id="`bgrEditorImg`"
                    :config="configUpImage"
                />
            </div>
            <slot name="note"></slot>
            <div
                class="box-editor"
                :class="`${configError?.error ? 'is-invalid-editor' : 'editor__content'} ${configEditor?.resize ? 'editor__resize' : ''}  ${configEditor?.is_autoresize_on ? 'autoresize' : ''} ${editorDisabled ? 'is-disabled' : ''}`"
            >
                <Editor
                    :id="id"
                    :ref="refEditor"
                    :SetContent="SetContent"
                    :model-events="safeModelEvents"
                    v-model="dataModel"
                    :api-key="apiKey ?? keyEditor"
                    cloud-channel="8"
                    @init="onEditorInit"
                    :init="configEditor"
                    :disabled="editorDisabled"
                />
            </div>

            <div v-if="configError?.error" class="invalid-feedback d-block">
                <span v-if="configError.isRequired">{{
                    $t('validateField.required', {
                        field: configError.errorField
                    })
                }}</span>
            </div>
        </div>
        <div v-else class="d-flex justify-content-center">
            <LoadingIcon />
        </div>

        <ModalCommon :id="'video-conversion-progress'" :config="configModalProgress">
            <div class="text-center p-3">
                <LoadingIcon class="d-flex justify-content-center align-items-center" />
                <p class="text-primary mt-2 fs-12">{{ loadingMessage }}</p>
            </div>
        </ModalCommon>
    </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import LoadingIcon from '@/components/Loading/main.vue';
import UploadImageCommon from '@/components/Uploads/Image/main.vue';
import ModalCommon from '@/components/Modal/main.vue';
import videoUploadMixin from '@/mixins/videoUploadMixin';

import { editorMethods, uploadMethods } from '@/state/helpers';
import {
    CONFIG_LAST_EDITOR,
    VIDEO_TEMPLATE_AUTOPLAY,
    IFRAME_TEMPLATE_AUTOPLAY,
    EXTENDED_VALID_ELEMENTS_AUTOPLAY,
    fixVideoSize,
    registerSyncFilters,
    handleObjectResized
} from './constants';

export default {
    name: 'EditorCommon',
    components: {
        Editor,
        LoadingIcon,
        UploadImageCommon,
        ModalCommon
    },
    mixins: [videoUploadMixin],
    props: {
        id: {
            type: [String, Number, null],
            default: 'editor-id'
        },
        model: {
            type: null,
            required: true,
            default: () => ''
        },
        refEditor: {
            type: [String, Number, null],
            default: 'editor'
        },
        SetContent: {
            type: String,
            default: 'SetContent'
        },
        modelEvents: {
            type: String,
            default: 'change keydown blur focus paste'
        },
        apiKey: {
            type: [String, Number, null],
            default: null
        },
        isLoading: {
            type: Boolean,
            default: () => false
        },
        isBackgroundImageEditor: {
            type: Boolean,
            default: () => false
        },
        isAutoplay: {
            type: Boolean,
            default: () => false
        },
        configInit: {
            type: Object,
            default: () => {}
        },
        configError: {
            type: Object,
            default: () => {
                return {
                    error: false,
                    valueSync: null,
                    isRequired: false,
                    errorField: '',
                    typesValidate: []
                };
            }
        }
    },
    data() {
        const baseConfig = {
            ...CONFIG_LAST_EDITOR,
            images_upload_handler: this.handleUploadImages,
            setup: this.setupEditor,
            file_picker_callback: this.handleFilePickerCallback
        };
        if (this.isAutoplay) {
            baseConfig.extended_valid_elements = EXTENDED_VALID_ELEMENTS_AUTOPLAY;
            baseConfig.video_template_callback = VIDEO_TEMPLATE_AUTOPLAY;
            baseConfig.iframe_template_callback = IFRAME_TEMPLATE_AUTOPLAY;
        }
        return {
            keyEditor:
                process.env.VUE_APP_ENV_OTHER !== 'local' ? process.env.VUE_APP_KEY_EDITOR : JSON.parse(localStorage.getItem('user'))?.key_editor,
            dataModel: '',
            configEditor: baseConfig,
            configUpImage: {
                maxSize: 5120,
                isPreview: false
            },
            editorInstance: null,
            backgroundEditor: null
        };
    },
    computed: {
        getStateEditor() {
            return this.$store.state.editor.configBgImgDefault;
        },
        safeModelEvents() {
            // Tự động chuyển đổi keydown thành input để gõ tiếng Việt mượt mà hơn cho toàn dự án
            return this.modelEvents ? this.modelEvents.replace('keydown', 'input') : 'change input blur focus paste';
        },
        editorDisabled() {
            return !!this.configInit?.disabled;
        }
    },
    methods: {
        ...editorMethods,
        ...uploadMethods,

        setupEditor(editor) {
            // Monkeypatch setMode for compatibility between @tinymce/tinymce-vue v3 and TinyMCE v7
            editor.setMode = (mode) => {
                if (editor.mode && typeof editor.mode.set === 'function') {
                    editor.mode.set(mode);
                }
            };

            editor.on('init', () => {
                // editor.execCommand('mceToggleEditor');
                this.onEditorInit(editor);
                if (editor.mode.get() === 'readonly') {
                    const body = editor.getBody();
                    if (body) {
                        body.style.setProperty('background-color', 'var(--editor-disabled-bg, #e9ecef)', 'important');
                        body.style.setProperty('cursor', 'not-allowed', 'important');
                    }
                }
            });
            editor.on('change', () => {
                this.$emit('change');
            });
            editor.on('blur', () => {
                this.$emit('blur');
            });
            editor.on('OpenWindow', function () {
                const tabs = document.querySelectorAll('.tox-dialog__body .tox-dialog__body-nav .tox-dialog__body-nav-item.tox-tab');
                tabs.forEach((tab) => {
                    if (tab?.textContent === 'General') {
                        tab.style.display = 'none';
                    }
                    if (tab?.textContent === 'Advanced' || tab?.textContent === '詳細') {
                        tab.style.display = 'none !important';
                        tab.classList.add('d-none');
                    }
                    if (tab.textContent === 'Upload') {
                        tab.click();
                    }
                });
            });

            editor.on('PreInit', () => {
                registerSyncFilters(editor, this.isAutoplay);
            });

            editor.on('SetContent NodeChange', () => {
                fixVideoSize(editor.getBody());
            });

            editor.on('ObjectResized', (e) => {
                handleObjectResized(e);
            });

            editor.on('EditableChange', (e) => {
                const body = editor.getBody();
                if (body) {
                    if (e.readonly) {
                        body.style.setProperty('background-color', 'var(--editor-disabled-bg, #e9ecef)', 'important');
                        body.style.setProperty('cursor', 'not-allowed', 'important');
                    } else {
                        body.style.backgroundColor = '';
                        body.style.cursor = '';
                    }
                }
            });

            this.editorInstance = editor;
        },

        onEditorInit() {
            this.$emit('init', this.editorInstance);
        },

        handleUploadImages(blobInfo, success, failure) {
            const promise = new Promise((resolve, reject) => {
                const formData = new FormData();
                formData.append('image', blobInfo.blob());
                fetch(`${process.env.VUE_APP_APIURL}/upload/image`, {
                    headers: {
                        Authorization: 'Bearer ' + window.$cookies.get('token')
                    },
                    method: 'POST',
                    body: formData
                })
                    .then((response) => response.json())
                    .then((data) => {
                        if (data?.code === 200) {
                            const url = data?.data?.path ?? '';
                            resolve(url);
                        } else {
                            reject(this.$t('uploadFile.maxSize5MB'));
                        }
                    })
                    .catch((error) => {
                        console.log('error', error);
                        reject(error?.message ?? 'Lỗi upload ảnh');
                    });
            });
            // ✅ TinyMCE v5–v7 (callback style)
            if (typeof success === 'function' && typeof failure === 'function') {
                promise.then(success).catch(failure);
            }
            // ✅ TinyMCE v8 (Promise REQUIRED)
            return promise;
        },

        handleFilePickerCallback(callback, value, meta) {
            if (meta.filetype !== 'media') return;
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = this.configEditor.allowed_extensions.map((ext) => `.${ext}`).join(',');
            input.onchange = async () => {
                const file = input.files[0];
                if (!file) return;
                const result = await this.baseHandleUploadVideo(file);
                if (result) {
                    callback(result.url || result.path, { title: result.name || file.name });
                }
                input.value = '';
            };
            input.click();
        },

        onHandleBackgroundImage() {
            const { backgroundImage, backgroundSize, backgroundPosition, backgroundRepeat, height } = this.getStateEditor;
            if (!this.editorInstance || !backgroundImage) return;
            const selectedContent = this.editorInstance.getContent();
            const backgroundDiv = `<div class="bgr--upload" style="background-image: url(${backgroundImage});background-size: ${backgroundSize};background-position: ${backgroundPosition};background-repeat: ${backgroundRepeat};min-height: 300px;max-height: ${height}px;height:${height}px; overflow: hidden;">${selectedContent}</div>`;
            this.editorInstance.setContent(backgroundDiv);
            this.$emit('update:model', backgroundDiv);
        },

        handleFileBackgroundEditor(file) {
            this.backgroundEditor = file;
            this.registerDataEditor({ backgroundImage: file.path, height: file.height, width: file.width });
            this.onHandleBackgroundImage();
        }
    },
    watch: {
        model: {
            handler: function (newModel) {
                const currentVal = newModel ?? '';
                if (currentVal !== this.dataModel) {
                    this.dataModel = currentVal;
                }
                let configErr = this.configError;
                if (configErr) {
                    configErr.error = false;
                    configErr.isRequired = false;
                    configErr.valueSync = currentVal;
                    this.$emit('update:configError', configErr);
                }
            },
            deep: true,
            immediate: true
        },

        dataModel: {
            handler: function (newModel) {
                if (newModel === this.model) return;

                let configErr = this.configError;
                configErr.error = false;
                configErr.isRequired = false;
                configErr.valueSync = newModel;

                this.$emit('update:configError', configErr);
                this.$emit('update:model', newModel);
            },
            immediate: true
        },

        configInit: {
            handler: function (newConfig) {
                let plugins = this.configEditor.plugins;
                if (newConfig?.is_autoresize_on) {
                    plugins = this.configEditor.plugins.includes('autoresize')
                        ? this.configEditor.plugins
                        : `${this.configEditor.plugins} autoresize`;
                }
                this.configEditor = {
                    ...this.configEditor,
                    ...newConfig,
                    plugins
                };
                if (this.editorInstance) {
                    const targetMode = newConfig?.disabled ? 'readonly' : 'design';
                    if (this.editorInstance.mode.get() !== targetMode) {
                        this.editorInstance.mode.set(targetMode);
                    }
                }
            },
            immediate: true,
            deep: true
        }
    }
};
</script>

<style lang="scss">
.box-editor {
    --editor-border-color: #ced4da;
    --editor-disabled-bg: #e9ecef;
    --editor-error-color: #ff3d60;
    --editor-disabled-text: #adb5bd;
    --editor-border-radius: 5px;

    border-radius: var(--editor-border-radius);
    .tox .tox-edit-area::before {
        border: unset;
    }
    .tox.tox-tinymce {
        border: none;
        min-height: 130px;
    }
    .tox-tinymce {
        border: 1px solid var(--editor-border-color);
        border-radius: var(--editor-border-radius);
    }
    &.editor__content {
        border: 1px solid var(--editor-border-color);
    }
    &.is-invalid-editor {
        border: 1px solid var(--editor-error-color);
    }
    &.editor__resize {
        .tox .tox-statusbar {
            display: flex !important;
            border-top: none;
            .tox-statusbar__text-container {
                display: none;
            }
        }
    }
    &.is-disabled {
        background-color: var(--editor-disabled-bg) !important;
        cursor: not-allowed !important;
        border-radius: var(--editor-border-radius);
        .tox-tinymce {
            pointer-events: none !important;
            background-color: var(--editor-disabled-bg) !important;
            border: 1px solid var(--editor-border-color);
            .tox-editor-container,
            .tox-editor-header,
            .tox-menubar,
            .tox-toolbar,
            .tox-toolbar__primary,
            .tox-toolbar__overflow,
            .tox-edit-area,
            .tox-edit-area__iframe,
            .tox-statusbar,
            .tox-sidebar-wrap,
            .tox-sidebar,
            .tox-editor-header > div {
                background-color: var(--editor-disabled-bg) !important;
                background-image: none !important;
            }
            .tox-menubar {
                border-bottom: 1px solid var(--editor-border-color);
            }
            .tox-mbtn,
            .tox-tbtn,
            .tox-split-button,
            .tox-split-button__chevron,
            .tox-split-button__action,
            .tox-tbtn--disabled,
            .tox-mbtn--disabled {
                background-color: var(--editor-disabled-bg) !important;
                color: var(--editor-disabled-text) !important;
                svg {
                    fill: var(--editor-disabled-text) !important;
                }
            }
        }
    }
}

.b-toaster {
    z-index: 99999 !important;
}

// Ensure video processing modal is on top of TinyMCE dialogs
#video-conversion-progress-modal___BV_modal_outer_ {
    z-index: 11000 !important;
}
</style>
